import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Shield, Wrench, Star } from "lucide-react";
import api, { MEDIA } from "../lib/api";

export default function Packages() {
    const [packages, setPackages] = React.useState([]);
    React.useEffect(() => { api.get("/packages").then(r => setPackages(r.data || [])); }, []);

    const featured = packages.find(p => p.featured);
    const rest = packages.filter(p => !p.featured);

    return (
        <div data-testid="packages-page">
            {/* Hero */}
            <section className="bg-foreground text-background rounded-b-[2rem] overflow-hidden">
                <div className="container-x py-14 md:py-20">
                    <div className="overline text-primary mb-3">Sisteme complete</div>
                    <h1 className="font-heading text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight max-w-2xl mb-4">
                        Pachete gata pentru instalare
                    </h1>
                    <p className="text-background/70 max-w-xl text-base sm:text-lg">
                        Echipamente profesionale + instalare realizată de specialiști certificați. Fără bătăi de cap, de la alegere până la punerea în funcțiune.
                    </p>
                    <div className="flex flex-wrap gap-6 mt-8">
                        {[
                            { icon: Shield, text: "Garanție 24 luni" },
                            { icon: Wrench, text: "Instalare inclusă" },
                            { icon: Star, text: "Echipă certificată" },
                        ].map(({ icon: Icon, text }) => (
                            <div key={text} className="flex items-center gap-2 text-background/80 text-sm">
                                <Icon className="h-4 w-4 text-primary" />
                                {text}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="container-x py-12 sm:py-16">
                {/* Featured package */}
                {featured && (
                    <div className="mb-10">
                        <div className="overline mb-4">Pachet recomandat</div>
                        <Link to={`/pachete/${featured.slug}`}
                            className="group grid grid-cols-1 lg:grid-cols-2 bg-card rounded-3xl overflow-hidden border border-border hover:border-primary/30 hover:shadow-xl transition-all duration-300 animate-fade-up"
                            data-testid={`pkg-featured-${featured.slug}`}>
                            <div className="aspect-[4/3] lg:aspect-auto bg-muted overflow-hidden relative">
                                {featured.image
                                    ? <img src={MEDIA(featured.image)} alt={featured.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    : <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5" />}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </div>
                            <div className="p-8 sm:p-10 flex flex-col justify-center">
                                {featured.includes_installation && (
                                    <div className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-4 w-fit">
                                        <Check className="h-3 w-3" /> Instalare inclusă
                                    </div>
                                )}
                                <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-3">{featured.name}</h2>
                                <p className="text-muted-foreground mb-6 line-clamp-3">{featured.description}</p>
                                <div className="flex items-end gap-3 mb-6">
                                    {featured.sale_price && featured.sale_price < featured.price && (
                                        <div className="text-muted-foreground line-through text-sm">{featured.price.toFixed(2)} RON</div>
                                    )}
                                    <div className="font-heading text-3xl font-bold text-primary">
                                        {(featured.sale_price || featured.price).toFixed(2)} <span className="text-sm font-normal text-foreground/60">RON</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                                    Vezi pachetul <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        </Link>
                    </div>
                )}

                {/* All packages grid */}
                {rest.length > 0 && (
                    <>
                        {featured && <div className="overline mb-4">Toate pachetele</div>}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {(featured ? rest : packages).map((p, i) => (
                                <Link key={p.package_id} to={`/pachete/${p.slug}`}
                                    className="group bg-card rounded-2xl overflow-hidden border border-border/60 hover:border-primary/30 hover:shadow-lg transition-all duration-300 animate-fade-up flex flex-col"
                                    style={{ animationDelay: `${i * 0.06}s` }}
                                    data-testid={`pkg-${p.slug}`}>
                                    <div className="aspect-[4/3] bg-muted overflow-hidden relative">
                                        {p.image
                                            ? <img src={MEDIA(p.image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            : <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5" />}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    </div>
                                    <div className="p-5 flex flex-col flex-1">
                                        {p.includes_installation && (
                                            <div className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-[10px] font-semibold px-2.5 py-1 rounded-full mb-3 w-fit">
                                                <Check className="h-2.5 w-2.5" /> Instalare inclusă
                                            </div>
                                        )}
                                        <div className="font-heading text-lg font-semibold mb-2">{p.name}</div>
                                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">{p.description}</p>
                                        <div className="flex items-end justify-between pt-3 border-t border-border/50">
                                            <div>
                                                {p.sale_price && p.sale_price < p.price && (
                                                    <div className="text-xs text-muted-foreground line-through">{p.price.toFixed(2)} RON</div>
                                                )}
                                                <div className="font-heading text-xl font-bold text-primary">
                                                    {(p.sale_price || p.price).toFixed(2)} <span className="text-xs font-normal text-foreground/60">RON</span>
                                                </div>
                                            </div>
                                            <span className="text-xs text-primary font-semibold flex items-center gap-1">
                                                Detalii <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </>
                )}

                {packages.length === 0 && (
                    <div className="py-20 text-center text-muted-foreground">
                        <div className="text-4xl mb-4">📦</div>
                        <p>Nu există pachete disponibile momentan.</p>
                    </div>
                )}

                {/* CTA */}
                <div className="mt-14 bg-foreground text-background rounded-2xl p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <div>
                        <div className="font-heading text-2xl sm:text-3xl mb-2">Nu găsești ce cauți?</div>
                        <p className="text-background/70 text-sm">Creăm pachete personalizate pentru orice buget și necesitate.</p>
                    </div>
                    <Link to="/contact" className="flex-shrink-0 bg-primary text-primary-foreground px-7 py-3.5 rounded-xl font-semibold hover:brightness-110 transition-all whitespace-nowrap">
                        Cere ofertă →
                    </Link>
                </div>
            </div>
        </div>
    );
}
